import { useEffect, useState } from "react";
import cx from "classnames";

import { isValueSet } from "common/utils";
import { toDate, toRelativeDate } from "common/utils/dates";
import { SubscriptionFrequency } from "common/types";
import { DeliveryMethodKind } from "common/types/fulfillments";
import CatalogImage from "common/components/CatalogImage";

import { useBreakpoints } from "client/common/hooks";
import ProductLineSelections from "client/products/components/ProductLineSelections";
import { useCartItemAddMutation, useControlledSubstancesSummaryMutation } from "client/carts/hooks";
import { CartCode, ControlledSubstancesSummary } from "client/carts/types";
import TextButton from "client/common/components/buttons/TextButton";
import { useClinic } from "client/clinics/hooks";
import Box from "client/common/components/layouts/Box";
import Address from "client/common/components/content/Address";
import DoctorImage from "client/common/components/images/DoctorImage";
import Banner from "client/common/components/content/Banner";
import Button from "client/common/components/buttons/Button";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import { urls } from "client/routes";
import {
  sendAddToCart,
  sendSelectItem,
  useSendPostHogAddToCart,
  useSendPostHogItemClick,
} from "client/analytics/utils";
import { ItemClickSource } from "client/analytics/types";
import TextChip from "client/common/components/content/TextChip";
import { useControlledSubstancesInfoDrawer } from "client/accounts/stackables";
import EmployeePricingNote from "client/common/components/content/EmployeePricingNote";

import { PrescriptionList } from "../types";
import PrescriptionStatus from "./PrescriptionStatus";
import { useInvalidatePrescriptions } from "../hooks/invalidate";

import styles from "../styles/Prescription.module.less";

type PrescriptionProps = { prescription: PrescriptionList };

const Prescription = ({ prescription }: PrescriptionProps) => {
  const clinic = useClinic();
  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const [isRefillModalOpen, setIsRefillModalOpen] = useState(false);
  const invalidatePrescriptions = useInvalidatePrescriptions();
  const { open: openControlledSubstancesInfoDrawer } = useControlledSubstancesInfoDrawer();

  const [subscription, setSubscription] = useState<SubscriptionFrequency | null>(
    prescription.recommended_frequency,
  );
  const [deliveryMethod, setDeliveryMethod] = useState(prescription.default_delivery_method);
  const [flavorSelection, setFlavorSelection] = useState(
    prescription.item.default_flavor_selection,
  );
  const sendPostHogAddToCart = useSendPostHogAddToCart();
  const sendPostHogItemClick = useSendPostHogItemClick();
  const {
    mutate: addCartItem,
    isLoading: isCartItemCreateLoading,
    error,
  } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (cartItemId, quantity, body) => {
      setIsRefillModalOpen(false);
      const price = subscription ? prescription.subscription_price : prescription.price;
      const discount = subscription
        ? prescription.original_price - prescription.subscription_price
        : 0;
      sendAddToCart({
        item: prescription.item,
        quantity,
        price,
        discount,
      });
      sendPostHogAddToCart({
        cart_id: typeof body.cart_id === "number" ? body.cart_id : null,
        cart_item_id: cartItemId,
        item_id: body.item,
        product_id: prescription.item.product.id,
        product_line_id: prescription.item.product.product_line.id,
        patient_id: body.patient_id ?? null,
        flavor_selection_id: body.flavor_selection ?? null,
        quantity,
        unit_price: price,
        discount,
      });
    },
  });

  const addToCart = () => {
    addCartItem({
      flavor_selection: flavorSelection || undefined,
      item: prescription.item.id,
      quantity: prescription.quantity,
      subscription: subscription || undefined,
      cart_id: CartCode.ACTIVE,
      is_pickup: deliveryMethod === DeliveryMethodKind.Pickup,
      patient_id: prescription.patient.id,
    });
  };
  const handleItemClick = () => {
    const price = subscription ? prescription.subscription_price : prescription.price;
    sendSelectItem(prescription.item, ItemClickSource.Prescription, price);
    sendPostHogItemClick(prescription.item, {
      source: ItemClickSource.Prescription,
      unit_price: price,
    });
  };

  const facts = [
    {
      label: "Pet Name",
      value: prescription.patient.name_display,
      className: styles.pet,
    },
    {
      label: "Prescribed on",
      value: toDate(prescription.prescribed_on),
      subValue: toRelativeDate(prescription.prescribed_on),
      className: styles.prescribed,
    },
    {
      label: "Expires on",
      value: prescription.expiration_date && toDate(prescription.expiration_date),
      subValue: prescription.expiration_date && toRelativeDate(prescription.expiration_date),
      className: styles.expires,
    },
    {
      label: "Fills",
      value: prescription.remaining_fills_display,
      subValue: !prescription.is_prn && prescription.fills_display,
      className: styles.refills,
    },
    {
      label: "Qty per fill",
      value: prescription.quantity_per_fill_display,
      className: styles.quantity,
    },
    {
      label: "Sig/Directions",
      value: prescription.directions,
      className: styles.sig,
    },
  ].filter((fact) => isValueSet(fact.value));

  const itemUrl = urls.products.detail({
    params: { id: prescription.item.product.product_line.id },
    search: {
      item: prescription.item.id,
      product: prescription.item.product.id,
    },
  });

  const fulfillment = prescription.fulfillment_options.find((o) => o.kind === deliveryMethod);

  const [controlledSubsSummary, setControlledSubsSummary] = useState<
    ControlledSubstancesSummary | undefined
  >(undefined);
  const { mutate: getControlledSubsInfo } = useControlledSubstancesSummaryMutation({
    onSuccess: setControlledSubsSummary,
  });

  useEffect(() => {
    getControlledSubsInfo({
      address_id: undefined,
      item_ids: [prescription.item.id],
    });
  }, [getControlledSubsInfo, prescription.item.id]);

  const selectionsNode = (
    <>
      {prescription.item.flavor_options.map((optionsGroup) => (
        <ProductLineSelections.OptionSelect
          key={optionsGroup.label}
          optionsGroup={optionsGroup}
          onChange={setFlavorSelection}
          value={flavorSelection}
        />
      ))}
      {prescription.fulfillment_options.length > 1 && (
        <ProductLineSelections.DeliveryMethodSelect
          fulfillmentOptions={prescription.fulfillment_options}
          value={deliveryMethod}
          onChange={setDeliveryMethod}
          showUnavailableOptions={true}
          messages={fulfillment?.messages ?? []}
        />
      )}
      {fulfillment?.is_available && (
        <ProductLineSelections.SubscriptionSelect
          item={{
            is_subscribable: prescription.item.is_subscribable,
            eligible_for_autoship_discounts: prescription.item.eligible_for_autoship_discounts,
            recommended_frequency: prescription.recommended_frequency,
            subscription_incentive_discount_percentage:
              prescription.subscription_incentive_discount_percentage,
            frequency_options: prescription.frequency_options,
            non_subscription_discounted_price: prescription.price,
            subscription_discounted_price: prescription.subscription_price,
            non_subscription_non_discounted_price: prescription.original_price,
            // Prescription prices are already computed at the fill quantity, so the
            // headline discounted fields are the correct per-tier price — no need
            // for per-quantity price breaks here.
            discounted_price_breaks: [],
          }}
          value={subscription}
          onChange={setSubscription}
          quantity={prescription.quantity}
        />
      )}
      {fulfillment?.is_low_stock && <ProductLineSelections.LowStock className={styles.lowStock} />}
    </>
  );

  const differentSizeButton = prescription.flexscriptions_enabled && (
    <Button
      className={styles.differentSizeButton}
      variant="secondary"
      dest={urls.products.detail({
        params: { id: prescription.item.product.product_line.id },
        search: {
          item: prescription.item.id,
          product: prescription.item.product.id,
        },
      })}
      disabled={!prescription.quantity || !prescription.is_available}
    >
      Select different size
    </Button>
  );

  const addToCartNode = fulfillment?.is_available ? (
    <>
      {prescription.should_show_employee_pricing_note && (
        <EmployeePricingNote isMapPriceAdjusted={prescription.is_map_price_adjusted} />
      )}
      <ProductLineSelections.AddSection error={error}>
        {!isTabletPortraitAndSmaller && differentSizeButton}
        <ProductLineSelections.AddToCartButton
          onSubmit={addToCart}
          disabled={!prescription.quantity || !prescription.is_available}
          isLoading={isCartItemCreateLoading}
          discountedSubtotal={
            prescription.quantity *
            (subscription ? prescription.subscription_price : prescription.price)
          }
        />
      </ProductLineSelections.AddSection>
    </>
  ) : null;

  return (
    <Box className={styles.container}>
      <div className={styles.main}>
        <CatalogImage.Link
          className={styles.image}
          image={prescription.item.image}
          size="large"
          alt={prescription.item.name}
          dest={itemUrl}
        />
        <div className={styles.content}>
          <div className={styles.item}>
            <div className={styles.names}>
              <TextButton dest={itemUrl} onClick={handleItemClick} className={styles.name}>
                {prescription.item.product.product_line.name}
              </TextButton>
              <span className={styles.subname}>
                {prescription.item.product_line_relative_name} · {prescription.patient.name_display}{" "}
                · Qty {prescription.quantity}
              </span>
            </div>
            <div className={styles.chips}>
              <PrescriptionStatus prescription={prescription} />
              {controlledSubsSummary?.is_controlled && (
                <TextChip variant="grey">
                  <TextButton onClick={openControlledSubstancesInfoDrawer}>
                    Controlled substance
                  </TextButton>
                </TextChip>
              )}
            </div>
          </div>
          {prescription.prescribed_by && (
            <Banner className={cx(styles.card, prescription.inactive && styles.inactive)}>
              <div
                className={cx(
                  styles.bannerHeader,
                  !prescription.inactive && styles.activeBannerHeader,
                )}
              >
                <DoctorImage
                  className={styles.doctorImage}
                  src={prescription.prescribed_by.image_url}
                />
                <Banner.Content
                  label={
                    <span className={styles.bannerLabel}>
                      {prescription.inactive ? (
                        <span>Veterinarian approval required for renewal</span>
                      ) : (
                        <span>Prescribed for {prescription.patient.name_display}</span>
                      )}
                    </span>
                  }
                  description={
                    <span className={styles.bannerDescription}>
                      {prescription.inactive ? (
                        <span>
                          {prescription.prescribed_by.name_display} will review your medication
                          request, and if deemed medically appropriate, renew your prescription.
                        </span>
                      ) : (
                        <>
                          <span className={styles.bannerDescriptionShort}>
                            By {prescription.prescribed_by.name_display}
                          </span>
                          <span className={styles.bannerDescriptionLong}>
                            If you would like to request a different size/strength, place your order
                            and {prescription.prescribed_by.name_display} will review your request.
                          </span>
                        </>
                      )}
                    </span>
                  }
                />
              </div>
              <div className={styles.info}>
                {facts.map((fact) => (
                  <div key={fact.label} className={cx(styles.fact, fact.className)}>
                    <div className={styles.value}>{isValueSet(fact.value) ? fact.value : "-"}</div>
                    <div className={styles.label}>{fact.label}</div>
                  </div>
                ))}
              </div>
            </Banner>
          )}
          <div className={styles.description}>
            {prescription.item.product.product_line.short_description}{" "}
            <TextButton dest={itemUrl} onClick={handleItemClick} hasUnderline={true} icon="right">
              View all product details
            </TextButton>
          </div>
          {prescription.is_available ? (
            <>
              {isTabletPortraitAndSmaller ? (
                <div className={styles.addToCartContainer}>
                  {differentSizeButton}
                  <Button onClick={() => setIsRefillModalOpen(true)}>Refill prescription</Button>
                  <Modal visible={isRefillModalOpen} onClose={() => setIsRefillModalOpen(false)}>
                    <ModalLayout>
                      <ModalLayout.Header
                        title="Refill prescription"
                        onClose={() => setIsRefillModalOpen(false)}
                      />
                      <ModalLayout.Content>{selectionsNode}</ModalLayout.Content>
                      <ModalLayout.Actions>
                        <div className={styles.addToCart}>{addToCartNode}</div>
                      </ModalLayout.Actions>
                    </ModalLayout>
                  </Modal>
                </div>
              ) : (
                <>
                  {selectionsNode}
                  {addToCartNode}
                  {(!fulfillment || !fulfillment.is_available) && (
                    <ProductLineSelections.Unavailable
                      item={prescription.item}
                      deliveryMethod={deliveryMethod}
                      onNotificationChange={invalidatePrescriptions}
                      className={styles.unavailable}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <ProductLineSelections.Unavailable
              item={prescription.item}
              deliveryMethod={deliveryMethod}
              onNotificationChange={invalidatePrescriptions}
              className={styles.unavailable}
            />
          )}
        </div>
      </div>
      <div className={styles.sider}>
        <div className={styles.facts}>
          {facts.map((fact) => (
            <div key={fact.label} className={cx(styles.fact, fact.className)}>
              <div className={styles.info}>
                <span className={styles.value}>{isValueSet(fact.value) ? fact.value : "-"}</span>
                {fact.subValue && <span className={styles.subValue}> ({fact.subValue})</span>}
              </div>
              <span className={styles.label}>{fact.label}</span>
            </div>
          ))}
        </div>
        {prescription.prescribed_by && (
          <div className={styles.doctor}>
            <div className={styles.image}>
              <DoctorImage
                className={styles.doctorImage}
                src={prescription.prescribed_by.image_url}
              />
            </div>
            <div className={styles.info}>
              <span className={styles.name}>{prescription.prescribed_by.name_display}</span>
              {clinic.primary_address && (
                <div className={styles.description}>
                  <Address address={clinic.primary_address} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};

export default Prescription;
